import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator, ScrollView } from 'react-native';
import { FaTimes, FaCopy, FaRedo, FaTwitter, FaTelegram, FaCheck, FaMagic } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import PercentageLoader from './PercentageLoader';
import { useTheme } from '../theme/ThemeContext';

let THEME = {};
const getStyles = () => StyleSheet.create({
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 },
    backdrop: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: THEME.overlay, backdropFilter: 'blur(8px)', zIndex: -1 },
    modalContainer: {
        width: '90%', maxWidth: 550, maxHeight: '85vh', backgroundColor: THEME.modalBg, borderRadius: 24,
        borderWidth: 1, borderColor: THEME.border, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
    },
    header: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 20,
        borderBottomWidth: 1, borderBottomColor: THEME.border, backgroundColor: THEME.bgCard,
    },
    headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    headerIcon: { color: '#0a84ff', fontSize: 20 },
    title: { color: THEME.textPrimary, fontSize: 20, fontWeight: '700', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    closeButton: { padding: 10, borderRadius: 12, backgroundColor: THEME.bgSurface },
    closeIcon: { color: THEME.textMuted, fontSize: 16 },
    content: { flex: 1 },
    contentContainer: { padding: 24 },
    loadingContainer: { alignItems: 'center', justifyContent: 'center', paddingVertical: 60 },
    loadingText: { color: THEME.textPrimary, fontSize: 18, fontWeight: '600', marginTop: 20 },
    loadingSubtext: { color: THEME.textSecondary, fontSize: 14, marginTop: 8 },
    errorContainer: { alignItems: 'center', justifyContent: 'center', paddingVertical: 40 },
    errorText: { color: '#ff453a', fontSize: 16, textAlign: 'center', marginBottom: 20 },
    retryButton: { flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'rgba(10, 132, 255, 0.15)', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 12 },
    retryIcon: { color: '#0a84ff', fontSize: 14 },
    retryText: { color: '#0a84ff', fontSize: 16, fontWeight: '600' },
    textAreaContainer: { backgroundColor: THEME.bgSurface, borderRadius: 16, borderWidth: 1, borderColor: THEME.border, padding: 16, marginBottom: 20 },
    textArea: { color: THEME.textPrimary, fontSize: 16, lineHeight: 24, minHeight: 150, textAlignVertical: 'top', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
    charCountContainer: { alignItems: 'flex-end', marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: THEME.border },
    charCount: { fontSize: 13, fontFamily: 'monospace' },
    actionsContainer: { flexDirection: 'row', gap: 12, marginBottom: 20 },
    actionButton: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, paddingHorizontal: 16, borderRadius: 12, backgroundColor: THEME.bgSurface, borderWidth: 1, borderColor: THEME.border },
    primaryButton: { backgroundColor: '#0a84ff', borderColor: '#0a84ff' },
    actionIcon: { color: THEME.textSecondary, fontSize: 16 },
    actionText: { color: THEME.textPrimary, fontSize: 14, fontWeight: '600' },
    tipContainer: { backgroundColor: 'rgba(255, 214, 10, 0.1)', borderRadius: 12, padding: 14, borderWidth: 1, borderColor: 'rgba(255, 214, 10, 0.2)' },
    tipText: { color: '#ffd60a', fontSize: 13, lineHeight: 18 },
    explanationContainer: { backgroundColor: 'rgba(99, 102, 241, 0.1)', borderRadius: 14, padding: 16, marginBottom: 16, borderWidth: 1, borderColor: 'rgba(99, 102, 241, 0.2)' },
    explanationHeader: { marginBottom: 10 },
    explanationTitle: { color: '#818cf8', fontSize: 14, fontWeight: '700' },
    explanationText: { color: THEME.textSecondary, fontSize: 14, lineHeight: 21 },
});
let styles = getStyles();

/**
 * ReactionModal - Modal for displaying and editing AI-generated reactions.
 * 
 * @param {boolean} isVisible - Whether the modal is visible
 * @param {function} onClose - Callback to close the modal
 * @param {string} reactionText - The generated reaction text
 * @param {boolean} isLoading - Whether a reaction is being generated
 * @param {function} onRegenerate - Callback to regenerate the reaction
 * @param {string} platform - The target platform ('twitter' or 'telegram')
 * @param {string} originalAuthor - Username of the original author (for opening reply)
 * @param {string} tweetId - Tweet ID for direct reply link
 */
const ReactionModal = ({
    isVisible,
    onClose,
    reactionText = '',
    explanation = '',
    isLoading = false,
    onRegenerate,
    platform = 'twitter',
    originalAuthor = '',
    tweetId = '',
    error = null,
}) => {
    const [editedText, setEditedText] = useState(reactionText);
    const [copied, setCopied] = useState(false);
    const [charCount, setCharCount] = useState(0);

    // Update edited text when reaction text changes
    useEffect(() => {
        setEditedText(reactionText);
        setCharCount(reactionText?.length || 0);
    }, [reactionText]);

    // Reset copied state after 2 seconds
    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => setCopied(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [copied]);

    const { t } = useTranslation();
    const { theme } = useTheme();
    Object.assign(THEME, theme);
    styles = getStyles();

    if (!isVisible) return null;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(editedText);
            setCopied(true);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleOpenPlatform = () => {
        let url;
        if (platform === 'twitter') {
            // Create Twitter intent URL with pre-filled text
            const encodedText = encodeURIComponent(editedText);
            if (tweetId && originalAuthor) {
                // Reply to specific tweet
                url = `https://twitter.com/intent/tweet?in_reply_to=${tweetId}&text=${encodedText}`;
            } else {
                // New tweet
                url = `https://twitter.com/intent/tweet?text=${encodedText}`;
            }
        } else if (platform === 'telegram') {
            // Open Telegram with pre-filled message (limited support)
            const encodedText = encodeURIComponent(editedText);
            url = `https://t.me/share/url?text=${encodedText}`;
        }

        if (url) {
            window.open(url, '_blank');
        }
    };

    const handleTextChange = (text) => {
        setEditedText(text);
        setCharCount(text.length);
    };

    const getCharCountColor = () => {
        if (charCount > 280) return '#ff453a';
        if (charCount > 240) return '#ff9f0a';
        return '#8e8e93';
    };

    return ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 199999 }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: THEME.overlay, backdropFilter: 'blur(8px)' }} onClick={onClose} />
            <View style={[styles.modalContainer, { zIndex: 1 }]}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <FaMagic style={styles.headerIcon} />
                        <Text style={styles.title}>{t('app.reactionModal.title')}</Text>
                    </View>
                    <TouchableOpacity onPress={onClose} style={styles.closeButton}>
                        <FaTimes style={styles.closeIcon} />
                    </TouchableOpacity>
                </View>

                {/* Content */}
                <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
                    {isLoading ? (
                        <View style={styles.loadingContainer}>
                            <PercentageLoader fontSize={48} color="#0a84ff" />
                            <Text style={styles.loadingText}>{t('app.reactionModal.craftingReaction')}</Text>
                            <Text style={styles.loadingSubtext}>
                                {t('app.reactionModal.craftingSubtext')}
                            </Text>
                        </View>
                    ) : error ? (
                        <View style={styles.errorContainer}>
                            <Text style={styles.errorText}>{error}</Text>
                            <TouchableOpacity
                                style={styles.retryButton}
                                onPress={onRegenerate}
                            >
                                <FaRedo style={styles.retryIcon} />
                                <Text style={styles.retryText}>{t('app.reactionModal.tryAgain')}</Text>
                            </TouchableOpacity>
                        </View>
                    ) : (
                        <>
                            {/* Editable Text Area */}
                            <View style={styles.textAreaContainer}>
                                <TextInput
                                    style={styles.textArea}
                                    value={editedText}
                                    onChangeText={handleTextChange}
                                    multiline
                                    placeholder={t('app.reactionModal.reactionPlaceholder')}
                                    placeholderTextColor={theme.inputPlaceholder}
                                />
                                <View style={styles.charCountContainer}>
                                    <Text style={[styles.charCount, { color: getCharCountColor() }]}>
                                        {t('app.reactionModal.charCount', { n: charCount })}
                                    </Text>
                                </View>
                            </View>

                            {/* Action Buttons */}
                            <View style={styles.actionsContainer}>
                                <TouchableOpacity
                                    style={styles.actionButton}
                                    onPress={handleCopy}
                                >
                                    {copied ? (
                                        <FaCheck style={{ fontSize: 16, color: '#30d158' }} />
                                    ) : (
                                        <FaCopy style={styles.actionIcon} />
                                    )}
                                    <Text style={styles.actionText}>
                                        {copied ? t('app.reactionModal.copied') : t('app.reactionModal.copy')}
                                    </Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    style={styles.actionButton}
                                    onPress={onRegenerate}
                                >
                                    <FaRedo style={styles.actionIcon} />
                                    <Text style={styles.actionText}>{t('app.reactionModal.regenerate')}</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    style={[styles.actionButton, styles.primaryButton]}
                                    onPress={handleOpenPlatform}
                                >
                                    {platform === 'twitter' ? (
                                        <FaTwitter style={{ fontSize: 16, color: '#fff' }} />
                                    ) : (
                                        <FaTelegram style={{ fontSize: 16, color: '#fff' }} />
                                    )}
                                    <Text style={[styles.actionText, { color: '#fff' }]}>
                                        {platform === 'twitter' ? t('app.reactionModal.openX') : t('app.reactionModal.openTelegram')}
                                    </Text>
                                </TouchableOpacity>
                            </View>

                            {/* Explanation Section */}
                            {explanation && (
                                <View style={styles.explanationContainer}>
                                    <View style={styles.explanationHeader}>
                                        <Text style={styles.explanationTitle}>{t('app.reactionModal.whyThisWorks')}</Text>
                                    </View>
                                    <Text style={styles.explanationText}>{explanation}</Text>
                                </View>
                            )}

                            {/* Tip */}
                            <View style={styles.tipContainer}>
                                <Text style={styles.tipText}>
                                    {t('app.reactionModal.editTip')}
                                </Text>
                            </View>
                        </>
                    )}
                </ScrollView>
            </View>
        </div>,
        document.body
    );
};


export default ReactionModal;
